import { GlobalOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd5';
import { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Dropdown, Tooltip } from 'components/ui';
import { type SupportedLanguage, languageNames, supportedLanguages } from 'utils/i18n';

type LanguageSwitcherProps = {
  /** Whether to show tooltip */
  showTooltip?: boolean;
  /** Additional CSS class */
  className?: string;
};

const getLanguageLabel = (language: SupportedLanguage) => (
  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <GlobalOutlined />
    {languageNames[language]}
  </span>
);

export const LanguageSwitcher = ({ showTooltip = true, className }: LanguageSwitcherProps) => {
  const { i18n, t } = useTranslation();
  const currentLanguage =
    supportedLanguages.find(language => i18n.language?.startsWith(language)) ?? 'en';

  const handleLanguageChange = useCallback(
    (language: SupportedLanguage) => {
      if (language === currentLanguage) {
        return;
      }

      // Persist the selection, then reload. LanguageDetector reads this key on
      // init, so after the reload the whole app — including translations that are
      // evaluated at module-load time (column/tab definitions in utils/constants
      // and data/*) — comes back fully in the chosen language. Without the reload
      // those module-level lookups keep the language captured at first import.
      localStorage.setItem('skyspecs-language', language);
      i18n.changeLanguage(language);
      window.location.reload();
    },
    [currentLanguage, i18n]
  );

  const languageChoices: MenuProps['items'] = useMemo(() => {
    const currentLanguageItem = {
      key: currentLanguage,
      label: getLanguageLabel(currentLanguage),
      onClick: () => handleLanguageChange(currentLanguage),
    };

    const additionalLanguages = supportedLanguages
      .filter(language => language !== currentLanguage)
      .map(language => ({
        key: language,
        label: getLanguageLabel(language),
        onClick: () => handleLanguageChange(language),
      }));

    if (!additionalLanguages.length) {
      return [currentLanguageItem];
    }

    return [currentLanguageItem, { type: 'divider' as const }, ...additionalLanguages];
  }, [currentLanguage, handleLanguageChange]);

  const languageName = (
    <Fragment>
      <GlobalOutlined />
      &nbsp;
      {languageNames[currentLanguage]}
    </Fragment>
  );

  const trigger = (
    <Button
      type="primary"
      ghost
      className={className}
      data-testid="language-switcher"
      style={{ border: 'none' }}
      aria-label={t('common.selectLanguage', 'Select Language')}
    >
      {languageName}
    </Button>
  );

  const switcher = (
    <Dropdown menu={{ items: languageChoices }} trigger={['hover', 'click']}>
      {trigger}
    </Dropdown>
  );

  if (showTooltip) {
    return (
      <Tooltip title={t('common.selectLanguage', 'Select Language')} placement="bottom">
        {switcher}
      </Tooltip>
    );
  }

  return switcher;
};

export default LanguageSwitcher;
